import React, {
	createContext,
	useContext,
	useCallback,
	useMemo,
	useRef,
	useState,
	type ReactNode,
	useEffect,
} from 'react';
import type { HoverPopoverState } from '../types.tsx';
import {
	HoverPopoverContentProvider,
	useHoverPopoverContent,
} from '../hover-popover-content-provider/index.tsx';

export interface HoverPopoverStateProviderProps {
	children?: ReactNode;
}

export interface HoverPopoverStateContextValue {
	hoverPopoverState: HoverPopoverState;
}

export interface HoverPopoverSetterContextValue {
	setPopoverClosed: () => void;
	setPopoverOpen: (popoverOpenParams: {
		relayFieldId: string;
		cell: HTMLTableCellElement;
		clearHoverFocusState: () => void;
	}) => void;
}

const HoverPopoverStateContext = createContext<HoverPopoverStateContextValue | undefined>(
	undefined,
);
const HoverPopoverSetterContext = createContext<HoverPopoverSetterContextValue | undefined>(
	undefined,
);

const defaultState: HoverPopoverState = {
	content: null,
	contentElement: null,
	cellRef: null,
	isOpen: false,
};

const HoverPopoverProviderLocal: React.FC<HoverPopoverStateProviderProps> = ({ children }) => {
	const [hoverPopoverState, setHoverPopoverState] = useState<HoverPopoverState>(defaultState);
	const unsubscribeRef = useRef<(() => void) | null>(null);

	const { getHoverPopoverContentById, subscribeHoverPopoverContentDeleteById } =
		useHoverPopoverContent();

	const setPopoverClosed = useCallback(() => {
		setHoverPopoverState(defaultState);
	}, [setHoverPopoverState]);

	/**
	 * When a popoover is opened, we subscribe to whether the content ref is deleted (i.e user switches from read to edit mode).
	 * If the content ref is deleted, we close the popover.
	 */
	const setPopoverOpen = useCallback(
		({
			relayFieldId,
			cell,
			clearHoverFocusState,
		}: {
			relayFieldId: string;
			cell: HTMLTableCellElement;
			clearHoverFocusState: () => void;
		}) => {
			const content = getHoverPopoverContentById(relayFieldId);

			if (!content) {
				return;
			}

			setHoverPopoverState({
				...content,
				isOpen: true,
				cellRef: cell,
			});

			unsubscribeRef.current = subscribeHoverPopoverContentDeleteById(
				relayFieldId,
				clearHoverFocusState,
			);
		},
		[setHoverPopoverState, getHoverPopoverContentById, subscribeHoverPopoverContentDeleteById],
	);

	useEffect(() => {
		return () => {
			unsubscribeRef.current?.();
		};
	}, []);

	const stateValue = useMemo(() => ({ hoverPopoverState }), [hoverPopoverState]);
	const setterValue = useMemo(
		() => ({ setPopoverClosed, setPopoverOpen }),
		[setPopoverClosed, setPopoverOpen],
	);

	return (
		<HoverPopoverStateContext.Provider value={stateValue}>
			<HoverPopoverSetterContext.Provider value={setterValue}>
				{children}
			</HoverPopoverSetterContext.Provider>
		</HoverPopoverStateContext.Provider>
	);
};

export const useHoverPopoverState = (): HoverPopoverStateContextValue => {
	const context = useContext(HoverPopoverStateContext);
	if (context === undefined) {
		throw new Error('useHoverPopover must be used within a HoverPopoverProvider');
	}
	return context;
};

export const useHoverPopoverSetter = (): HoverPopoverSetterContextValue => {
	const context = useContext(HoverPopoverSetterContext);
	if (context === undefined) {
		throw new Error('useHoverPopover must be used within a HoverPopoverProvider');
	}
	return context;
};

export const HoverPopoverStateProvider = ({ children }: HoverPopoverStateProviderProps) => {
	return (
		<HoverPopoverContentProvider>
			<HoverPopoverProviderLocal>{children}</HoverPopoverProviderLocal>
		</HoverPopoverContentProvider>
	);
};
